import React, { useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import '../../styles/layout/MobileMenuCard.css';

interface MenuItem {
  label: string;
  href?: string;
  to?: string;
  icon?: string;
  onClick?: () => void;
  subItems?: MenuItem[];
}

interface MobileMenuCardProps {
  isOpen: boolean;
  onClose: () => void;
  menuItems: MenuItem[];
  toggleButtonRef?: React.RefObject<HTMLButtonElement | null>;
}

const MobileMenuCard: React.FC<MobileMenuCardProps> = ({
  isOpen,
  onClose,
  menuItems,
  toggleButtonRef
}) => {
  const navigate = useNavigate();
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Don't close if clicking on the toggle button
      if (toggleButtonRef?.current && toggleButtonRef.current.contains(event.target as Node)) {
        return;
      }
      
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose, toggleButtonRef]);

  const handleItemClick = (item: MenuItem) => {
    if (item.onClick) {
      item.onClick();
    }
    if (item.to && !item.subItems) {
      navigate(item.to);
      onClose();
    }
    if (item.href && !item.subItems) {
      window.open(item.href, '_blank', 'noopener noreferrer');
      onClose();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          ref={menuRef}
          className="mobile-menu-card"
          initial={{ 
            opacity: 0, 
            y: 10, 
            scale: 0.95
          }}
          animate={{ 
            opacity: 1, 
            y: 0, 
            scale: 1
          }}
          exit={{ 
            opacity: 0, 
            y: 10, 
            scale: 0.95
          }}
          transition={{ 
            duration: 0.2,
            ease: [0.4, 0, 0.2, 1]
          }}
        >
          <div className="mobile-menu-content">
            {menuItems.map((item, index) => (
              <div key={index} className="mobile-menu-section">
                <div 
                  className="mobile-menu-item"
                  onClick={() => handleItemClick(item)}
                >
                  {item.icon && <span className="mobile-menu-icon">{item.icon}</span>}
                  <span className="mobile-menu-label">{item.label}</span>
                </div>
                
                {item.subItems && (
                  <div className="mobile-menu-subitems">
                    {item.subItems.map((subItem, subIndex) => (
                      <div 
                        key={subIndex}
                        className="mobile-menu-subitem"
                        onClick={() => handleItemClick(subItem)}
                      >
                        {subItem.icon && <span className="mobile-menu-icon">{subItem.icon}</span>}
                        <span className="mobile-menu-label">{subItem.label}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default MobileMenuCard;